import { useMemo, useState } from "react";
import { Building2, MapPin, Radio, Search } from "lucide-react";
import { BusinessCard } from "@/components/businesses/BusinessCard";
import { Input, Select } from "@/components/ui/Input";
import { Workspace, WorkspaceSection, FieldLabel } from "@/components/ui/Workspace";
import { ChipGroup } from "@/components/ui/FilterPanel";
import {
  defaultBusinessFilters,
  filterBusinesses,
  type BusinessListEntry,
} from "@/lib/business-analytics";
import { uniqueValues } from "@/lib/utils";

export function BusinessesExplorer({ entries }: { entries: BusinessListEntry[] }) {
  const [filters, setFilters] = useState(defaultBusinessFilters());

  const types = useMemo(() => uniqueValues(entries, (entry) => entry.type), [entries]);
  const countries = useMemo(() => uniqueValues(entries, (entry) => entry.country), [entries]);
  const filtered = useMemo(() => filterBusinesses(entries, filters), [entries, filters]);

  function resetAll() {
    setFilters(defaultBusinessFilters());
  }

  return (
    <Workspace>
      <WorkspaceSection className="border-b border-border/30 p-4 sm:p-5">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <FieldLabel icon={<Search className="h-3 w-3" />}>Search</FieldLabel>
            <Input
              placeholder="Name, email, type…"
              value={filters.search}
              onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
            />
          </div>
          <div>
            <FieldLabel icon={<Building2 className="h-3 w-3" />}>Type</FieldLabel>
            <Select
              value={filters.type}
              onChange={(e) => setFilters((f) => ({ ...f, type: e.target.value }))}
            >
              <option value="">All types</option>
              {types.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <FieldLabel icon={<MapPin className="h-3 w-3" />}>Country</FieldLabel>
            <Select
              value={filters.country}
              onChange={(e) => setFilters((f) => ({ ...f, country: e.target.value }))}
            >
              <option value="">All countries</option>
              {countries.map((country) => (
                <option key={country} value={country}>
                  {country}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <FieldLabel icon={<Radio className="h-3 w-3" />}>Connected service</FieldLabel>
            <Select
              value={filters.hasConnectedService}
              onChange={(e) => setFilters((f) => ({ ...f, hasConnectedService: e.target.value }))}
            >
              <option value="">All businesses</option>
              <option value="yes">Has application</option>
              <option value="no">No application</option>
            </Select>
          </div>
        </div>
      </WorkspaceSection>

      <WorkspaceSection border className="grid lg:grid-cols-[220px_1fr]">
        <aside className="border-b border-border/30 p-4 lg:border-b-0 lg:border-r lg:border-border/30">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-sm font-bold">Filters</h3>
            <button
              type="button"
              onClick={resetAll}
              className="text-xs font-bold text-accent-link transition hover:text-foreground"
            >
              Reset all
            </button>
          </div>

          <div className="space-y-4">
            <ChipGroup
              label="Approval status"
              options={["Pending", "Approved", "Rejected", "No application"]}
              value={
                filters.approvalStatus === "pending"
                  ? "Pending"
                  : filters.approvalStatus === "approved"
                    ? "Approved"
                    : filters.approvalStatus === "rejected"
                      ? "Rejected"
                      : filters.approvalStatus === "none"
                        ? "No application"
                        : ""
              }
              onChange={(value) =>
                setFilters((f) => ({
                  ...f,
                  approvalStatus:
                    value === "Pending"
                      ? "pending"
                      : value === "Approved"
                        ? "approved"
                        : value === "Rejected"
                          ? "rejected"
                          : value === "No application"
                            ? "none"
                            : "",
                }))
              }
            />
          </div>
        </aside>

        <section className="p-4 sm:p-5">
          <p className="mb-5 text-sm text-muted">
            <span className="font-bold text-foreground">{filtered.length}</span> businesses
          </p>

          {filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 text-center">
              <p className="font-bold">No matches</p>
              <p className="mt-1 text-sm text-muted">Adjust your filters to see results.</p>
            </div>
          ) : (
            <div className="stagger-children grid gap-4 sm:grid-cols-2">
              {filtered.map((entry) => (
                <BusinessCard key={entry.id} entry={entry} />
              ))}
            </div>
          )}
        </section>
      </WorkspaceSection>
    </Workspace>
  );
}
